const process = {"env":{},"argv":[]};

import { injectable, Container } from 'inversify';
import { create } from 'zustand';
import { subscribeWithSelector, persist } from 'zustand/middleware';
import { areDeepEqual } from '@writefull/utils';
import { configureCache, GrowthBook } from '@growthbook/growthbook';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import * as React from 'react';
import { createContext, useMemo, useContext, useCallback, useState, useEffect } from 'react';
import { jsx } from 'react/jsx-runtime';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

let FetchClient = class FetchClient {
    client;
    constructor(client) {
        this.client = client;
        this.client.interceptors.response.use(undefined, async (err) => {
            const { config, message } = err;
            if (!config?.retry) {
                throw err;
            }
            // retry while Network timeout or Network Error
            if (!(message.includes('timeout') || message.includes('Network Error'))) {
                throw err;
            }
            config.retry -= 1;
            // delay 1 second before retry
            await new Promise((r) => {
                setTimeout(r, config.retryDelay ?? 1000);
            });
            return this.client(config);
        });
    }
    getHeaders() {
        return this.client.defaults.headers;
    }
    updateHeaders(headers) {
        this.client.defaults.headers = { ...this.client.defaults.headers, ...headers };
    }
    async get(url, abortSignal, options = {}) {
        try {
            const response = await this.client.get(url, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async post(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.post(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    async put(url, data, abortSignal, options = {}) {
        try {
            const response = await this.client.put(url, data, { signal: abortSignal, ...options });
            return response?.data;
        }
        catch (error) {
            console.error(error);
            this.handleError(error);
            return Promise.reject(error);
        }
    }
    handleError(error) {
        if (!error) {
            throw {
                message: 'UNKNOWN_ERROR',
                status: 500,
            };
        }
        const { response } = error;
        if (!response) {
            throw {
                message: error,
                status: 500,
            };
        }
        const { data, status, headers } = response;
        throw {
            message: data?.error ?? data?.message ?? 'UNKNOWN_ERROR',
            status,
            headers,
        };
    }
};
FetchClient = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Function])
], FetchClient);

const expiresAt = (expiresIn) => {
    const timeNow = Math.round(Date.now() / 1000);
    return timeNow + parseInt(expiresIn, 10);
};
let AuthBackendApiImpl = class AuthBackendApiImpl {
    fetchClient;
    authHost;
    nlpHost;
    constructor(fetchClient, authHost, nlpHost) {
        this.fetchClient = fetchClient;
        this.authHost = authHost;
        this.nlpHost = nlpHost;
    }
    async signInWithEmail(email, password) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/auth`, {
                email,
                password,
            });
            if (data.expiresIn) {
                data.expiresAt = expiresAt(data.expiresIn);
            }
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async refreshAccessToken(refreshToken) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/idToken`, {
                refreshToken,
            });
            const session = this.parseRefreshAccessTokenResponse(data);
            return { data: session, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async getUserDetails(idToken) {
        try {
            const user = (await this.fetchClient.get(`${this.authHost}/v2/user`, undefined, {
                params: {
                    t: Date.now(),
                },
                headers: { 'firebase-token': idToken },
            }));
            const isPremium = user.emailVerified
                ? await this.isAPremiumUser(idToken)
                : { isPremium: false, isPaid: false };
            const data = { ...user, ...isPremium };
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    async runActionOnDictionary(idToken, action, target, replacement) {
        try {
            const data = await this.fetchClient.post(`${this.authHost}/v2/user/dictionary`, {
                idToken,
                action,
                target,
                replacement,
            });
            return { data, error: null };
        }
        catch (error) {
            return { data: null, error };
        }
    }
    parseRefreshAccessTokenResponse(response) {
        const { id_token: idToken, refresh_token: refreshToken, user_id: uid, expires_in: expiresIn, } = response;
        const session = {
            idToken,
            refreshToken,
            expiresIn: parseInt(expiresIn, 10),
            uid,
            expiresAt: 0,
        };
        if (session.expiresIn) {
            session.expiresAt = expiresAt(session.expiresIn);
        }
        return session;
    }
    async isAPremiumUser(idToken) {
        if (!idToken) {
            return { isPremium: false, isPaid: false, source: null, reason: null };
        }
        const data = await this.fetchClient.post(`${this.nlpHost}/isPremium`, {}, undefined, {
            headers: { 'firebase-token': idToken },
        });
        const { isPremium, isPaid, source, reason } = data;
        return { isPremium, isPaid, source, reason };
    }
};
AuthBackendApiImpl = __decorate([
    injectable(),
    __metadata("design:paramtypes", [FetchClient, String, String])
], AuthBackendApiImpl);

const createSelectors = (_store) => {
    const store = _store;
    store.use = {};
    for (const k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }
    return store;
};

let StoreBase = class StoreBase {
    store;
    constructor(initialState, actions, config) {
        this.store = this.createStore(initialState, actions, config);
    }
    getStore() {
        return createSelectors(this.store);
    }
    getState() {
        return this.store.getState();
    }
    getInitialState() {
        return this.store.getInitialState();
    }
    subscribe(...args) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return this.store.subscribe(...args);
    }
    setState(state) {
        this.store.setState(state);
    }
    deinit() {
        this.resetState();
    }
    resetState() {
        return this.store.setState(this.store.getInitialState());
    }
    createStore(initialState, actions, config) {
        const middlewares = config?.middlewares ?? [];
        const storeCreator = (set, get) => ({
            ...initialState,
            ...actions(set, get),
        });
        const storeWithMiddlewares = middlewares.reduce((acc, middleware) => middleware.middleware(acc, middleware.options), storeCreator);
        return create()(subscribeWithSelector(storeWithMiddlewares));
    }
};
StoreBase = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Function, Object])
], StoreBase);

let PersistentStoreBase = class PersistentStoreBase extends StoreBase {
    config;
    localStorageName;
    constructor(initialState, actions, config) {
        if (!config) {
            throw new Error('PersistentStoreBase requires a config object');
        }
        const storageName = `wf.${config.storageName}${config.storageIdentifier ? `__${config.storageIdentifier}` : ''}`;
        config.middlewares = [
            ...(config.middlewares ?? []),
            {
                middleware: persist,
                options: {
                    name: storageName,
                    version: config.version,
                    ...(config.propsToPersist
                        ? {
                            partialize: (state) => config.propsToPersist.reduce((acc, key) => ({ ...acc, [key]: state[key] }), {}),
                        }
                        : {}), // in case of persisting the whole state, partialize is not needed
                    ...(config.merge ? { merge: config.merge } : {}),
                    ...(config.migrate ? { migrate: config.migrate } : {}),
                },
            },
        ];
        super(initialState, actions, config);
        this.config = config;
        this.localStorageName = storageName;
    }
    deinit() {
        if (this.config.resetStateOnDeinit ?? true) {
            super.resetState();
            this.store.persist.clearStorage();
        }
    }
    getName() {
        return this.config.storageName;
    }
    getLocalStorageName() {
        return this.localStorageName;
    }
};
PersistentStoreBase = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Function, Object])
], PersistentStoreBase);

const initialState = {
    migrated: [],
};
const actions = (set) => ({
    setMigrated: (name) => set((state) => ({
        ...state,
        migrated: state.migrated.includes(name) ? state.migrated : [...state.migrated, name],
    })),
});
const migrationsStore = create()(persist((set) => ({
    ...initialState,
    ...actions(set),
}), {
    name: 'wf.synchronizable-store-migrations',
}));
let SynchronizableStoreBase = class SynchronizableStoreBase extends PersistentStoreBase {
    synchronizeCallback = null;
    synchronizeStateChangesUnsubscriber = null;
    async init(synchronizeCallback) {
        this.synchronizeCallback = synchronizeCallback;
        this.initSynchronizeListener();
        await this.checkPendingMigrations();
    }
    deinit() {
        this.synchronizeStateChangesUnsubscriber?.();
        super.deinit();
    }
    synchronize(newState) {
        const currentState = this.store.getState();
        this.store.setState({ ...currentState, ...newState, _isSynchronized: true });
        this.markAsMigrated();
    }
    getIsSynchronized() {
        return this.store.getState()._isSynchronized;
    }
    initSynchronizeListener() {
        this.synchronizeStateChangesUnsubscriber?.(); // clean possible previous subscription in case of more thant 1 initialization
        this.synchronizeStateChangesUnsubscriber = this.store.subscribe((current, prev) => {
            if (prev === undefined) {
                return; // initial run
            }
            const propsToPersist = this.config.propsToPersist ??
                Object.keys(current).filter((k) => typeof this.getState()[k] !== 'function' &&
                    k !== '_isSynchronized'); // In case of not given propsToPersist, persist all state, so we get prop keys current entity, without functions. Exclude _isSynchronized from persistence
            propsToPersist.forEach((k) => {
                const value = current[k];
                if (k !== '_isSynchronized' && !areDeepEqual(value, prev?.[k])) {
                    try {
                        this.synchronizeCallback?.({ [k]: value });
                    }
                    catch (e) {
                        console.error('Error synchronizing changes', e);
                    }
                }
            });
        });
    }
    async checkPendingMigrations() {
        try {
            const { migrated } = migrationsStore.getState();
            if (migrated.includes(this.getLocalStorageName())) {
                return;
            }
            await this.migrateFromLocalStorage();
            this.markAsMigrated();
        }
        catch (e) {
            console.error('Error checking migrations', e);
        }
    }
    async migrateFromLocalStorage() {
        const localStorageState = this.getState();
        const initialStoreState = this.getInitialState();
        const propsToPersist = this.config.propsToPersist ??
            Object.keys(localStorageState).filter((k) => k !== '_isSynchronized'); // In case of not given propsToPersist, persist all state, so we get prop keys from saved entity in localStorage. Exclude _isSynchronized from persistence
        const initialPersistState = propsToPersist.reduce((acc, k) => {
            if (k === '_isSynchronized') {
                return acc; // Skip _isSynchronized
            }
            const value = localStorageState[k];
            // only migrate if the value is different from the initial state
            if (!areDeepEqual(value, initialStoreState[k])) {
                acc[k] = localStorageState[k];
            }
            return acc;
        }, {});
        if (Object.keys(initialPersistState).length) {
            await this.synchronizeCallback?.(initialPersistState);
        }
    }
    markAsMigrated() {
        migrationsStore.getState().setMigrated(this.getLocalStorageName());
    }
};
SynchronizableStoreBase = __decorate([
    injectable()
], SynchronizableStoreBase);

let MetricsBackendApiImpl = class MetricsBackendApiImpl {
    fetchClient;
    apiHost;
    store;
    constructor(fetchClient, apiHost, store) {
        this.fetchClient = fetchClient;
        this.apiHost = apiHost;
        this.store = store;
    }
    sendLegacyMetric(data, abortSignal) {
        return this.fetchClient.post(this.apiHost, data, abortSignal);
    }
    sendMetric(data, abortSignal) {
        const token = this.store.getState().session?.idToken;
        return this.fetchClient.post(this.apiHost, data, abortSignal, {
            headers: { 'api-version': '2', 'firebase-token': token },
        });
    }
};
MetricsBackendApiImpl = __decorate([
    injectable(),
    __metadata("design:paramtypes", [FetchClient, String, StoreBase])
], MetricsBackendApiImpl);

class AuthBackendApi {
}

function isPersistentStoreInstance(instance) {
    return Object.getPrototypeOf(instance) instanceof PersistentStoreBase;
}
function isSynchronizableInstance(instance) {
    return Object.getPrototypeOf(instance) instanceof SynchronizableStoreBase;
}
class IoCContainer extends Container {
    bindSingleton(serviceIdentifier, factory) {
        const factoryIdentifier = Symbol(serviceIdentifier.toString());
        this.bind(factoryIdentifier).toFactory((context) => {
            let instance = null;
            return () => {
                instance ??= factory(context);
                return instance;
            };
        });
        this.bind(serviceIdentifier).toDynamicValue((c) => {
            const factoryMethod = c.container.get(factoryIdentifier);
            return factoryMethod();
        });
    }
    bindStore(serviceIdentifier, instance) {
        this.bind(serviceIdentifier).toConstantValue(instance);
        this.bind(StoreBase).toConstantValue(instance);
        if (isPersistentStoreInstance(instance)) {
            this.bind(PersistentStoreBase).toConstantValue(instance);
        }
        if (isSynchronizableInstance(instance)) {
            this.bind(SynchronizableStoreBase).toConstantValue(instance);
        }
    }
}

const FEATURE_CACHE_STORAGE_KEY = 'wf.features';
let FeatureManager = class FeatureManager {
    featureIds;
    growthbook;
    experiments = [];
    subscriptions = [];
    constructor(apiHost, clientKey, decryptionKey, developmentMode, featureIds) {
        this.featureIds = featureIds;
        if (!apiHost || !clientKey || !decryptionKey) {
            throw new Error('Missing required configuration');
        }
        if (!featureIds.length) {
            throw new Error('No features given');
        }
        configureCache({
            cacheKey: FEATURE_CACHE_STORAGE_KEY, // The localStorage key the cache will be stored under
            staleTTL: 1000 * 60, // Consider features stale after this much time (60 seconds default)
            maxAge: 1000 * 60 * 60 * 24, // Cached features older than this will be ignored (24 hours default)
            maxEntries: 10, // For Remote Eval only - limit the number of cache entries (~1 entry per user)
            disableIdleStreams: false, // When `false`, we add a `visibilitychange` listener to disable SSE when the page is idle
            idleStreamInterval: 20000, // Consider a page "idle" when it is hidden for this long (default 20 seconds)
            disableCache: false, // Set to `true` to completely disable both in-memory and persistent caching
        });
        this.growthbook = new GrowthBook({
            apiHost,
            clientKey,
            decryptionKey,
            enableDevMode: developmentMode ?? false,
        });
    }
    async init(userId, attributes) {
        await this.growthbook.setAttributes({
            id: userId,
            ...(attributes ?? {}),
        });
        await this.growthbook.init({
            streaming: true,
        });
        this.experiments = this.featureIds
            .map((f) => {
            const result = this.growthbook.evalFeature(f);
            if (!result?.experiment?.key || !result?.experimentResult) {
                return null;
            }
            return {
                name: result.experiment.key,
                variation: result.experimentResult.variationId,
            };
        })
            .filter((r) => r !== null);
    }
    isOn(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    getFeatureValue(featureId) {
        this.ensureFeature(featureId);
        return this.growthbook.getFeatureValue(featureId, null);
    }
    async updateAttributes(attributes) {
        await this.growthbook.setAttributeOverrides({
            ...this.growthbook.getAttributes(),
            ...attributes,
        });
    }
    addFeatureChangeSubscription(callback) {
        this.subscriptions = [...this.subscriptions, callback];
        // https://docs.growthbook.io/lib/js#re-rendering-when-features-change
        this.setRenderer();
        return () => {
            this.removeFeatureChangeSubscription(callback);
        };
    }
    getUserExperimentsWithVariations() {
        return this.experiments;
    }
    deinit() {
        localStorage.removeItem(FEATURE_CACHE_STORAGE_KEY);
    }
    isValidFeature(featureId) {
        return this.featureIds.includes(featureId);
    }
    ensureFeature(featureId) {
        if (!this.isValidFeature(featureId)) {
            throw new Error(`Feature '${featureId}' is not present in the list of registered features`);
        }
    }
    removeFeatureChangeSubscription(callback) {
        this.subscriptions = this.subscriptions.filter((c) => c !== callback);
        this.setRenderer();
    }
    setRenderer() {
        this.growthbook.setRenderer(() => {
            this.subscriptions.forEach((c) => c());
        });
    }
};
FeatureManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [String, String, String, Boolean, Object])
], FeatureManager);

const oneWeek = 7 * 24 * 60 * 60 * 1000; // milliseconds in a week
const nonObjectValueKey = '__value';
const expiresAtKey = '__expiresAt';
let IDBManager = class IDBManager {
    openDB;
    config;
    errorNotifier;
    db;
    constructor(openDB, config, errorNotifier) {
        this.openDB = openDB;
        this.config = config;
        this.errorNotifier = errorNotifier;
        this.config = {
            ...config,
            expirationTime: config.expirationTime ?? oneWeek,
            expiresAtKey: config.expiresAtKey ?? expiresAtKey,
            nonObjectValueKey: config.nonObjectValueKey ?? nonObjectValueKey,
        };
    }
    async initDB() {
        try {
            const { config, errorNotifier } = this;
            this.db = await this.openDB(config.dbName, config.version, {
                upgrade(database) {
                    Object.keys(config.stores).forEach((store) => {
                        try {
                            if (database.objectStoreNames.contains(store)) {
                                return;
                            }
                            database.createObjectStore(store);
                        }
                        catch (error) {
                            errorNotifier.notifyError('DBError', `Error upgrading db: ${error}`);
                            console.error('error creating store', error);
                        }
                    });
                },
            });
        }
        catch (error) {
            console.error('error opening db', error);
            this.errorNotifier.notifyError('DBError', error.message);
        }
    }
    async getDB() {
        if (!this.db) {
            await this.initDB();
        }
        return this.db;
    }
    async get(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        const value = await this.db.get(storeName, key);
        return this.parseValue(value);
    }
    async getAll(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const values = await this.db.getAll(storeName);
        return values.map(this.parseValue);
    }
    async put(storeName, data) {
        if (!this.db) {
            await this.initDB();
        }
        const isExpirable = this.config.stores[storeName]?.expirable;
        const expirationDate = new Date().getTime() + this.config.expirationTime;
        const records = Array.isArray(data) ? data : [data];
        records.map((record) => {
            const { key, value } = record;
            if (!key) {
                console.warn('cache put: no key');
                return null;
            }
            return this.db.put(storeName, {
                ...(typeof value === 'object' && !Array.isArray(value)
                    ? value
                    : { [nonObjectValueKey]: value }),
                ...(isExpirable ? { [this.config.expiresAtKey]: expirationDate } : {}),
            }, key);
        });
    }
    async delete(storeName, key) {
        if (!this.db) {
            await this.initDB();
        }
        this.db.delete(storeName, key);
    }
    async clear(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        await this.db.clear(storeName);
    }
    async purge(storeName) {
        if (!this.db) {
            await this.initDB();
        }
        const keys = await this.db?.getAllKeys(storeName);
        const promises = keys?.map(async (key) => {
            const record = (await this.db?.get(storeName, key));
            if (record?.[this.config.expiresAtKey] &&
                record[this.config.expiresAtKey] < new Date().getTime()) {
                await this.db?.delete(storeName, key);
            }
        });
        await Promise.all(promises ?? []);
    }
    async purgeAll() {
        if (!this.db) {
            await this.initDB();
        }
        const promises = Object.keys(this.config.stores).map(async (store) => {
            const result = await this.purge(store);
            return result;
        });
        await Promise.all(promises);
    }
    async initPurgeExpiration() {
        if (!this.db) {
            await this.initDB();
        }
        await this.purgeAll();
        setInterval(() => this.purgeAll(), this.config.expirationTime);
    }
    async close() {
        if (!this.db) {
            await this.initDB();
        }
        this.db.close();
    }
    parseValue(value) {
        if (value && !!value[nonObjectValueKey]) {
            return value[nonObjectValueKey];
        }
        return value;
    }
};
IDBManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Object, Object])
], IDBManager);

let NetworkManager = class NetworkManager {
    isOnline = window.navigator.onLine;
    listeners;
    constructor() {
        this.listeners = new Map();
    }
    init() {
        window.addEventListener('online', this.handleOnline.bind(this));
        window.addEventListener('offline', this.handleOffline.bind(this));
    }
    off(event, callback) {
        if (this.listeners.has(event)) {
            this.listeners.get(event).delete(callback);
        }
    }
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(callback);
        if (event === 'statusChange') {
            callback({
                isOnline: this.isOnline,
            });
        }
    }
    emit(event, payload) {
        if (this.listeners.has(event)) {
            this.listeners.get(event)?.forEach((callback) => callback(payload));
        }
    }
    handleOnline() {
        this.emit('online', { isOnline: true });
    }
    handleOffline() {
        this.emit('offline', { isOnline: false });
    }
};
NetworkManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [])
], NetworkManager);

let StoreSynchronizerManager = class StoreSynchronizerManager {
    synchronizableStores;
    customerPortalBackendApi;
    sessionStore;
    constructor(synchronizableStores, customerPortalBackendApi, sessionStore) {
        this.synchronizableStores = synchronizableStores;
        this.customerPortalBackendApi = customerPortalBackendApi;
        this.sessionStore = sessionStore;
    }
    init() {
        const session = this.sessionStore.getState().session?.user?.config?.settings;
        this.synchronize(session);
        this.listenToChanges();
    }
    deinit() {
        this.synchronizableStores.forEach((store) => {
            store.deinit();
        });
    }
    synchronize(settings) {
        // Always synchronize all stores, even if no backend data exists
        this.synchronizableStores.forEach((store) => {
            const storeName = store.getName();
            const storeSettings = settings?.[storeName] ?? {};
            store.synchronize(storeSettings);
        });
    }
    listenToChanges() {
        this.synchronizableStores.forEach((store) => {
            store.init((settings) => this.customerPortalBackendApi.updateUserSettings(store.getName(), settings));
        });
    }
};
StoreSynchronizerManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Array, Object, Object])
], StoreSynchronizerManager);

let BugsnagErrorMonitoring = class BugsnagErrorMonitoring {
    apiKey;
    appVersion;
    releaseStage;
    isLoaded = false;
    constructor(apiKey, appVersion, releaseStage) {
        this.apiKey = apiKey;
        this.appVersion = appVersion;
        this.releaseStage = releaseStage;
        this.init();
    }
    setUserId(userId) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.setUser(userId);
    }
    notifyError(name, message) {
        if (!this.isLoaded) {
            return;
        }
        Bugsnag.notify({ name, message });
    }
    isRunning() {
        return this.isLoaded;
    }
    createErrorBoundary() {
        if (!this.isLoaded) {
            return undefined;
        }
        const plugin = Bugsnag.getPlugin('react');
        if (!plugin) {
            return undefined;
        }
        return plugin.createErrorBoundary(React);
    }
    init() {
        if (!this.apiKey?.length) {
            return;
        }
        Bugsnag.start({
            apiKey: this.apiKey,
            appVersion: this.appVersion,
            releaseStage: this.releaseStage || 'development',
            plugins: [new BugsnagPluginReact()],
            onBreadcrumb: (breadcrumb) => {
                this.cleanSensitiveData(breadcrumb);
                return true;
            },
        });
        this.isLoaded = true;
    }
    cleanSensitiveData(breadcrumb) {
        if (breadcrumb.message === 'UI click' && breadcrumb.metadata?.targetText) {
            breadcrumb.metadata.targetText = `${breadcrumb.metadata?.targetText.length} characters`;
        }
    }
};
BugsnagErrorMonitoring = __decorate([
    injectable(),
    __metadata("design:paramtypes", [String, String, String])
], BugsnagErrorMonitoring);

let QuotaManager = class QuotaManager {
    backendAdapter;
    authManager;
    quotaStore;
    networkStore;
    backendQuotaTypes;
    checkBackendQuotaTimeouts = {};
    checkFrontEndTimeout;
    constructor(backendAdapter, authManager, quotaStore, networkStore, backendQuotaTypes) {
        this.backendAdapter = backendAdapter;
        this.authManager = authManager;
        this.quotaStore = quotaStore;
        this.networkStore = networkStore;
        this.backendQuotaTypes = backendQuotaTypes;
        if (!backendQuotaTypes.length) {
            throw new Error('No backend quota types given');
        }
    }
    async init() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        if (this.authManager.user()?.isPremium) {
            this.backendQuotaTypes.forEach((quotaType) => {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            });
        }
        await this.check();
    }
    deinit() {
        this.quotaStore.getState().clearFrontEndQuotaExceeded();
        this.backendQuotaTypes.forEach((quotaType) => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        });
    }
    async check() {
        await this.checkFrontEndQuota();
        this.checkBackEndQuotas();
    }
    handleBackendQuotaExceeded(quotaType, retryAfterSeconds) {
        clearTimeout(this.checkBackendQuotaTimeouts[quotaType]);
        const retryAfter = retryAfterSeconds ?? 3 * 60 * 60;
        this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, retryAfter);
        this.checkBackendQuotaTimeouts[quotaType] = setTimeout(() => {
            this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
        }, retryAfter * 1_000);
    }
    async checkFrontEndQuota() {
        clearTimeout(this.checkFrontEndTimeout);
        if (this.authManager.user()?.isPremium) {
            this.quotaStore.getState().clearFrontEndQuotaExceeded();
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
            return;
        }
        const quotaIsExceededFromStore = this.quotaStore.getState().frontEndQuotaExceeded &&
            Date.now() < this.quotaStore.getState().frontEndQuotaRestoreTime;
        if (quotaIsExceededFromStore) {
            this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), this.quotaStore.getState().frontEndQuotaRestoreTime - Date.now());
            return;
        }
        try {
            const response = await this.backendAdapter.checkUserQuota();
            const quotaIsExceeded = response.suggest_frontend?.exceeded && (response.suggest_frontend?.ttlSeconds ?? 0) > 0;
            if (quotaIsExceeded) {
                this.quotaStore.getState().setFrontEndQuotaExceeded(response.suggest_frontend.ttlSeconds);
                this.checkFrontEndTimeout = setTimeout(() => this.checkFrontEndQuota(), response.suggest_frontend.ttlSeconds * 1_000);
            }
            else {
                this.quotaStore.getState().clearFrontEndQuotaExceeded();
            }
            this.networkStore.getState().setIsQuotaCheckAvailable(true);
        }
        catch {
            this.networkStore.getState().setIsQuotaCheckAvailable(false);
        }
    }
    checkBackEndQuotas() {
        this.backendQuotaTypes.forEach((quotaType) => {
            const restoreTime = this.quotaStore.getState().backEndQuotaRestoreTime[quotaType];
            if (restoreTime && restoreTime > Date.now()) {
                const secondsToRestore = Math.floor((restoreTime - Date.now()) / 1_000);
                this.quotaStore.getState().setBackEndQuotaExceeded(quotaType, secondsToRestore);
            }
            else {
                this.quotaStore.getState().clearBackEndQuotaExceeded(quotaType);
            }
        });
    }
};
QuotaManager = __decorate([
    injectable(),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object])
], QuotaManager);

const IoCContext = createContext({ container: null });
function IoCContextProvider({ container, children = '', }) {
    const value = useMemo(() => ({ container }), [container]);
    return jsx(IoCContext.Provider, { value: value, children: children });
}
function useGetDependency(identifier) {
    const { container } = useContext(IoCContext);
    if (!container) {
        throw new Error('Container is not provided');
    }
    const dependency = container.get(identifier);
    return dependency;
}
function useGetDependencyScoped(identifier) {
    const { container } = useContext(IoCContext);
    if (!container) {
        throw new Error('Container is not provided');
    }
    const dependency = useMemo(() => container.get(identifier), [container, identifier]);
    return dependency;
}
function useTryGetDependency(identifier) {
    const { container } = useContext(IoCContext);
    if (!container) {
        return null;
    }
    try {
        return container.get(identifier);
    }
    catch {
        return null;
    }
}
function useTryGetDependencyScoped(identifier) {
    const { container } = useContext(IoCContext);
    const dependency = useMemo(() => {
        if (!container) {
            return null;
        }
        try {
            return container.get(identifier);
        }
        catch {
            return null;
        }
    }, [container, identifier]);
    return dependency;
}

const FeatureContext = createContext(undefined);
function FeatureContextProvider({ children = '' }) {
    const featureManager = useGetDependency(FeatureManager);
    const isFeatureOn = useCallback((id) => featureManager.isOn(id) ?? null, [featureManager]);
    const getFeatureValue = useCallback((id) => featureManager.getFeatureValue(id) ?? null, [featureManager]);
    const updateAttributes = useCallback(async (attributes) => {
        await featureManager.updateAttributes(attributes);
    }, [featureManager]);
    const onFeatureChanged = useCallback((callback) => featureManager.addFeatureChangeSubscription(callback), [featureManager]);
    const valueContext = useMemo(() => ({
        isFeatureOn,
        getFeatureValue,
        updateAttributes,
        onFeatureChanged,
    }), [isFeatureOn, getFeatureValue, updateAttributes, onFeatureChanged]);
    return jsx(FeatureContext.Provider, { value: valueContext, children: children });
}
function useFeatureContext() {
    const context = useContext(FeatureContext);
    if (context === undefined) {
        throw new Error('useFeatureContext must be used within a FeatureContext');
    }
    return context;
}
function useFeatureValue(featureId) {
    const { getFeatureValue, onFeatureChanged } = useFeatureContext();
    const [value, setValue] = useState(null);
    useEffect(() => {
        setValue(getFeatureValue(featureId));
        // Subscribe to feature changes to force an state update so react can re-render
        const unsubscribe = onFeatureChanged(() => {
            setValue(getFeatureValue(featureId));
        });
        return () => {
            unsubscribe?.();
        };
    }, [featureId, getFeatureValue, onFeatureChanged]);
    return value;
}
function useIsFeatureOn(featureId) {
    const { isFeatureOn, onFeatureChanged } = useFeatureContext();
    const [isOn, setIsOn] = useState(null);
    useEffect(() => {
        setIsOn(isFeatureOn(featureId));
        // Subscribe to feature changes to force an state update so react can re-render
        const unsubscribe = onFeatureChanged(() => {
            setIsOn(isFeatureOn(featureId));
        });
        return () => {
            unsubscribe?.();
        };
    }, [featureId, isFeatureOn, onFeatureChanged, setIsOn]);
    return isOn;
}

export { AuthBackendApi, AuthBackendApiImpl, BugsnagErrorMonitoring, FEATURE_CACHE_STORAGE_KEY, FeatureContext, FeatureContextProvider, FeatureManager, FetchClient, IDBManager, IoCContainer, IoCContextProvider, MetricsBackendApiImpl, NetworkManager, PersistentStoreBase, QuotaManager, StoreBase, StoreSynchronizerManager, SynchronizableStoreBase, useFeatureContext, useFeatureValue, useGetDependency, useGetDependencyScoped, useIsFeatureOn, useTryGetDependency, useTryGetDependencyScoped };
//# sourceMappingURL=index.js.map
